import React, { useEffect, useState, useMemo } from "react";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { ThemeProvider, createTheme, CssBaseline } from "@mui/material";
// eslint-disable-next-line no-unused-vars
import { getDesignTokens, TokenManager } from "@smfa/utils";
import AOS from "aos";
import { fetchAllPageContent } from "./actions/pagecontent.actions";
import ScrollToTop from "./utils/ScrollToTop";
import ErrorBoundary from "./components/Common/ErrorBoundary";
import "./App.css";

// Pages
//------------------------------------------------------------------------------
import Home from "./pages/index";
import AboutUs from "./pages/about";
import Contact from "./pages/contact";
import Meeting from "./pages/meeting";
import Patients from "./pages/patients";
import Benefitsmembership from "./pages/benefitsmembership";
import Memberhighlight from "./components/memberhighlight";
import Membershipcategories from "./pages/membershipcategories";
import SignUp from "./pages/signup";
import Getinvolved from "./pages/getinvolved";
import ResearchCommittee from "./pages/research-committee";
import IntersocietyCommittee from "./components/intersociety-committee";
import FiestaSession from "./pages/fiesta-session";
import Guidelines from "./pages/guidelines";
import MeetingRegister from "./pages/meeting-register";
//import Affiliates from "./pages/affiliates";
import MemberLogin from "./pages/login";
import PrivacyPolicy from "./pages/privacy-policy";
import CancelPolicy from "./pages/cancel-policy";
import PaymentComplete from "./components/PaymentComplete";
import SecuredRoute from "@smfa/utils/src/components/SecuredRoute";
import MemberDashboard from "./pages/dashboard";
import Profile from "./pages/profile";
import Donate from "./pages/donate";
import MemberDirectory from "./pages/member-directory";
import NewsAcetaminophenFetal from "./pages/news/acetaminophen-fetal-surgery";

/**
 * Non-matching route component
 * @returns {JSX.Element}
 * @component
 */
const NoMatch = () => <Navigate to="/" />;

/**
 * Main App component
 * @returns {JSX.Element}
 * @component
 */
const App = () => {
  const dispatch = useDispatch();
  const auth = useSelector((state) => state?.auth) || {};
  const userdata = useSelector((state) => state?.user) || {};
  // eslint-disable-next-line no-unused-vars
  const isAuthenticated = Boolean(auth?.isAuthenticated);
  const [appInitialized, setAppInitialized] = useState(false);

  // eslint-disable-next-line react-hooks/exhaustive-deps, no-unused-vars
  const userData = auth?.user || userdata?.user || {};

  // Log the auth state for debugging with more detail
  // log.debug("[App] Auth state:", {
  //   isAuthenticated,
  //   authState: auth,
  //   userdataState: userdata,
  //   userDataEval: userData,
  //   token: TokenManager.getToken(),
  // });

  const [mode, setMode] = useState("light");
  // theme with the appropriate mode
  const theme = useMemo(() => createTheme(getDesignTokens(mode)), [mode]);

  // Routes - must be stable across re-renders; RouterProvider does not
  // support swapping the router object, so recreating it on every render
  // breaks in-flight navigations (e.g. login -> /member-dashboard).
  //----------------------------------------------------------------------------
  const router = useMemo(
    () =>
      createBrowserRouter(
        [
          {
            element: (
              <ScrollToTop>
                <Outlet />
              </ScrollToTop>
            ),
            children: [
              { path: "/", element: <Home /> },
              { path: "/about-us", element: <AboutUs /> },
              { path: "/contact", element: <Contact /> },
              { path: "/patients", element: <Patients /> },
              { path: "/benefitsmembership", element: <Benefitsmembership /> },
              { path: "/memberhighlight", element: <Memberhighlight /> },
              {
                path: "/membershipcategories",
                element: <Membershipcategories />,
              },
              { path: "/signup", element: <SignUp /> },
              { path: "/donate", element: <Donate /> },
              { path: "/getinvolved", element: <Getinvolved /> },
              { path: "/research-committee", element: <ResearchCommittee /> },
              {
                path: "/intersociety-committee",
                element: <IntersocietyCommittee />,
              },
              // { path: "/affiliates", element: <Affiliates /> },
              { path: "/guidelines", element: <Guidelines /> },
              { path: "/register", element: <MeetingRegister /> },
              { path: "/login", element: <MemberLogin /> },
              { path: "/payment-complete", element: <PaymentComplete /> },
              { path: "/privacy-policy", element: <PrivacyPolicy /> },
              { path: "/cancel-policy", element: <CancelPolicy /> },
              { path: "/meeting", element: <Meeting /> },
              { path: "/fiesta-sessions", element: <FiestaSession /> },
              {
                path: "/news/acetaminophen-fetal-surgery",
                element: <NewsAcetaminophenFetal />,
              },
              // Protected member routes
              //------------------------------------------------------------------
              {
                element: <SecuredRoute />,
                children: [
                  { path: "/dashboard", element: <MemberDashboard /> },
                  { path: "/profile", element: <Profile /> },
                  { path: "/member-directory", element: <MemberDirectory /> },
                ],
              },
              // Default ---------------------------------------------------------
              { path: "*", element: <NoMatch /> },
            ],
          },
        ],
        { basename: "/" },
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  // Theme toggling function to be passed to components that need it
  // eslint-disable-next-line no-unused-vars
  const toggleColorMode = () =>
    setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));

  // Initialize app on mount
  useEffect(() => {
    if (appInitialized) {
      return;
    }

    // Prefetch content of all pages
    fetchAllPageContent(dispatch);

    // Animate-on-scroll
    AOS.init();

    setAppInitialized(true);
  }, [dispatch, appInitialized]);

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ErrorBoundary>
        <RouterProvider router={router} />
      </ErrorBoundary>
    </ThemeProvider>
  );
};

export default App;
